<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'

const props = defineProps({
  cardSquare: { type: Object, required: true },
  canMark: { type: Boolean, default: false },
})

const emit = defineEmits(['toggle', 'needs-refit'])

const labelEl = ref(null)
const MIN_FONT_PX = 8
const MAX_FONT_PX = 80

let useWrap = false

function measureBestSize() {
  const el = labelEl.value
  if (!el) return null
  const parent = el.parentElement
  if (!parent) return null
  const maxW = parent.clientWidth
  const maxH = parent.clientHeight
  if (maxW <= 0 || maxH <= 0) return null

  const debug = props.cardSquare?.grid_row === 0 && props.cardSquare?.grid_col === 0
  if (debug) {
    const button = parent.parentElement // .bingo-square
    const cs = button ? getComputedStyle(button) : null
    console.log('[bingo-fit] measure start', {
      id: props.cardSquare?.id,
      label: props.cardSquare?.square?.label,
      parentW: maxW,
      parentH: maxH,
      buttonW: button?.clientWidth,
      buttonH: button?.clientHeight,
      buttonRectW: button?.getBoundingClientRect().width,
      buttonRectH: button?.getBoundingClientRect().height,
      boxSizing: cs?.boxSizing,
      aspectRatio: cs?.aspectRatio,
      padding: cs?.padding,
      fontsStatus: document.fonts?.status,
      innerWidth: window.innerWidth,
    })
  }

  el.style.display = 'inline-block'

  const search = () => {
    let lo = MIN_FONT_PX
    let hi = MAX_FONT_PX
    let best = null
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2)
      el.style.fontSize = `${mid}px`
      const rect = el.getBoundingClientRect()
      if (rect.width <= maxW && rect.height <= maxH) {
        best = mid
        lo = mid + 1
      } else {
        hi = mid - 1
      }
    }
    return best
  }

  el.style.maxWidth = 'none'
  el.style.whiteSpace = 'nowrap'
  const nowrapBest = search() ?? 0

  el.style.whiteSpace = 'normal'
  el.style.maxWidth = `${maxW}px`
  const wrapBest = search() ?? 0

  const hasSpaces = /\s/.test(el.textContent ?? '')
  useWrap = hasSpaces && wrapBest > nowrapBest
  const chosen = Math.max(useWrap ? wrapBest : nowrapBest, MIN_FONT_PX)

  if (debug) {
    // scrollWidth at chosen size reveals overflow the fitter didn't see
    // (single long word wider than max-width).
    el.style.fontSize = `${chosen}px`
    const overflowW = el.scrollWidth
    const rect = el.getBoundingClientRect()
    console.log('[bingo-fit] measure done', {
      id: props.cardSquare?.id,
      nowrapBest,
      wrapBest,
      useWrap,
      chosen,
      renderedW: rect.width,
      scrollW: overflowW,
      overflowingByPx: overflowW - maxW,
    })
  }

  return chosen
}

function applySize(size) {
  const el = labelEl.value
  if (!el || size == null) return
  if (useWrap) {
    el.style.whiteSpace = 'normal'
    const parent = el.parentElement
    if (parent) el.style.maxWidth = `${parent.clientWidth}px`
  } else {
    el.style.whiteSpace = 'nowrap'
    el.style.maxWidth = 'none'
  }
  el.style.fontSize = `${size}px`
}

defineExpose({ measureBestSize, applySize })

let resizeObserver = null

onMounted(async () => {
  const debug = props.cardSquare?.grid_row === 0 && props.cardSquare?.grid_col === 0
  await nextTick()
  if (debug) console.log('[bingo-fit] trigger: mount', { fontsStatus: document.fonts?.status })
  emit('needs-refit')
  if (document.fonts?.ready) {
    document.fonts.ready.then(() => {
      if (debug) console.log('[bingo-fit] trigger: fonts.ready', { fontsStatus: document.fonts?.status })
      emit('needs-refit')
    })
  }
  const parent = labelEl.value?.parentElement
  if (parent && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver((entries) => {
      if (debug) {
        const e = entries[0]
        console.log('[bingo-fit] trigger: resize', {
          contentW: e?.contentRect?.width,
          contentH: e?.contentRect?.height,
        })
      }
      emit('needs-refit')
    })
    resizeObserver.observe(parent)
  }
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})

watch(
  () => props.cardSquare?.square?.label,
  async () => {
    await nextTick()
    emit('needs-refit')
  },
)
</script>

<template>
  <button
    class="bingo-square"
    :class="[`bingo-square--${cardSquare.square.square_type}`, { marked: cardSquare.marked, clickable: canMark }]"
    :data-card-square-id="cardSquare.id"
    :disabled="!canMark"
    @click="canMark && $emit('toggle')"
  >
    <div class="square-content">
      <img
        v-if="cardSquare.square.square_type === 'image'"
        :src="cardSquare.square.image"
        :alt="cardSquare.square.label"
        class="square-image"
      />
      <span v-else ref="labelEl" class="square-label">{{ cardSquare.square.label }}</span>
    </div>
    <transition name="checkmark">
      <div v-if="cardSquare.marked" class="checkmark-overlay">
        <svg viewBox="0 0 24 24" class="checkmark-icon" fill="none" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
        </svg>
      </div>
    </transition>
  </button>
</template>

<style scoped>
.bingo-square {
  position: relative;
  aspect-ratio: 1;
  background: var(--bingo-gradient);
  border: 2px solid var(--bingo-border, rgba(255, 255, 255, 0.3));
  border-radius: var(--bingo-square-radius, 8px);
  box-shadow: var(--bingo-square-shadow, 0 0 12px rgba(255, 255, 255, 0.15));
  overflow: hidden;
  clip-path: var(--bingo-square-clip, none);
  cursor: default;
  padding: 0;
}

.bingo-square.clickable {
  cursor: pointer;
}

.bingo-square.clickable:hover {
  box-shadow: 0 0 16px rgba(255, 255, 255, 0.35);
}

.bingo-square.clickable:active {
  transform: scale(0.95);
}

.square-content {
  position: absolute;
  inset: min(40px, 25%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.bingo-square:has(.square-image) .square-content {
  inset: 0;
}

.square-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.square-label {
  font-family: var(--bingo-font-heading);
  font-size: 2rem;
  line-height: 1;
  font-weight: bold;
  color: var(--bingo-square-text, white);
  text-transform: var(--bingo-text-transform, uppercase);
  text-align: center;
  overflow-wrap: normal;
  word-break: normal;
  background: var(--bingo-label-bg, transparent);
  padding: var(--bingo-label-padding, 0);
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
}

.bingo-square--number .square-label {
  white-space: nowrap;
}

.checkmark-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
}

.checkmark-icon {
  width: 60%;
  height: 60%;
  color: white;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.checkmark-enter-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.checkmark-leave-active {
  transition: opacity 0.15s ease;
}

.checkmark-enter-from {
  opacity: 0;
  transform: scale(0.5);
}

.checkmark-leave-to {
  opacity: 0;
}
</style>
